import React, { useState, useEffect, useRef } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, ExternalLink } from 'lucide-react';
import { mockData } from '../mock';

const Contact = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    console.log('Form submitted:', formData);
    setIsSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', email: '', company: '', message: '' });
    }, 3000);
  };

  const contactMethods = [
    {
      icon: Phone,
      label: 'Telefone',
      value: mockData.contact.phone,
      href: `tel:${mockData.contact.phone.replace(/\D/g, '')}`,
      description: 'Ligue agora para uma consulta'
    },
    {
      icon: Mail,
      label: 'E-mail',
      value: mockData.contact.email,
      href: `mailto:${mockData.contact.email}`,
      description: 'Envie sua dúvida por e-mail'
    },
    {
      icon: MapPin,
      label: 'Localização',
      value: mockData.contact.address,
      href: '#',
      description: 'Nossa localização'
    }
  ];

  const openWhatsApp = () => {
    const message = encodeURIComponent(
      `Olá! Estou interessado em saber mais sobre os serviços de segurança da informação da NefilinSEC, especialmente sobre pentests e análise de vulnerabilidades.`
    );
    window.open(`https://wa.me/${mockData.contact.whatsapp}?text=${message}`, '_blank');
  };

  return (
    <section id="contact" ref={sectionRef} className="py-24 bg-gradient-to-b from-black to-gray-900 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="cyber-grid opacity-5" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-l from-cyan-500/10 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-r from-blue-500/10 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-400/30 rounded-full glass-effect mb-6">
            <Mail className="w-4 h-4 text-cyan-400" />
            <span className="text-sm font-medium text-cyan-400">Entre em Contato</span>
          </div>
          
          <h2 className="heading-1 mb-6">
            <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              Vamos conversar sobre sua segurança
            </span>
          </h2>
          
          <p className="body-large text-gray-300 max-w-3xl mx-auto">
            Entre em contato para solicitar um orçamento personalizado e discutir 
            suas necessidades de segurança cibernética.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left Side - Contact Form */}
          <div className={`${isVisible ? 'animate-fadeInLeft' : 'opacity-0'}`}>
            <div className="glass-effect border border-cyan-400/30 rounded-lg p-8">
              <h3 className="heading-2 text-white mb-6">
                Solicite um <span className="text-cyan-400">Orçamento</span>
              </h3>

              {!isSubmitted ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name Input */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-black/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all duration-300"
                      placeholder="Seu nome completo"
                    />
                  </div>

                  {/* Email Input */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      E-mail *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-black/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all duration-300"
                      placeholder="seu@email.com"
                    />
                  </div>

                  {/* Company Input */}
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                      Empresa
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-black/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all duration-300"
                      placeholder="Nome da sua empresa"
                    />
                  </div>

                  {/* Message Textarea */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                      Mensagem *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      className="w-full px-4 py-3 bg-black/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all duration-300 resize-none"
                      placeholder="Descreva suas necessidades de segurança..."
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="w-full btn-primary group"
                  >
                    <Send className="w-5 h-5" />
                    <span>Enviar Mensagem</span>
                  </button>
                </form>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                  <h3 className="heading-3 text-green-400 mb-2">Mensagem Enviada!</h3>
                  <p className="body-medium text-gray-300">
                    Obrigado pelo contato. Responderemos em breve.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Right Side - Contact Information */}
          <div className={`space-y-8 ${isVisible ? 'animate-fadeInRight' : 'opacity-0'}`}>
            {/* Contact Methods */}
            <div className="space-y-6">
              {contactMethods.map((method, index) => {
                const IconComponent = method.icon;
                
                return (
                  <div
                    key={index}
                    className="group glass-effect border border-white/10 rounded-lg p-6 hover:border-cyan-400/50 transition-all duration-500"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 glass-effect border border-cyan-400/30 rounded-full flex items-center justify-center group-hover:border-cyan-400 group-hover:scale-110 transition-all duration-300">
                        <IconComponent className="w-6 h-6 text-cyan-400" />
                      </div>
                      
                      <div className="flex-1">
                        <h4 className="heading-3 text-white mb-1 group-hover:text-cyan-400 transition-colors duration-300">
                          {method.label}
                        </h4>
                        <p className="body-small text-gray-400 mb-2">{method.description}</p>
                        <a
                          href={method.href}
                          className="body-medium text-cyan-400 hover:text-white transition-colors duration-300 flex items-center space-x-2"
                        >
                          <span>{method.value}</span>
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* WhatsApp CTA */}
            <div className="glass-effect border border-green-400/30 rounded-lg p-6 bg-green-400/5">
              <h4 className="heading-3 text-white mb-4">
                <span className="text-green-400">WhatsApp</span> - Resposta Rápida
              </h4>
              <p className="body-medium text-gray-300 mb-6">
                Para atendimento imediato, entre em contato pelo WhatsApp. 
                Responderemos rapidamente suas dúvidas.
              </p>
              
              <button
                onClick={openWhatsApp}
                className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 hover:scale-105 flex items-center justify-center space-x-2"
              >
                <span>📱</span>
                <span>Conversar no WhatsApp</span>
              </button>
            </div>

            {/* Business Info */}
            <div className="glass-effect border border-cyan-400/30 rounded-lg p-6">
              <h4 className="heading-3 text-white mb-4">Informações da Empresa</h4>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">CNPJ:</span>
                  <span className="text-white">{mockData.contact.cnpj}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Website:</span>
                  <span className="text-cyan-400">{mockData.contact.website}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Horário:</span>
                  <span className="text-white">Seg-Sex 9h às 18h</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className={`text-center mt-16 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '0.8s' }}>
          <div className="glass-effect border border-cyan-400/30 rounded-lg p-8 max-w-3xl mx-auto">
            <h3 className="heading-2 text-white mb-4">
              Pronto para <span className="text-cyan-400">proteger</span> seu negócio?
            </h3>
            
            <p className="body-large text-gray-300 mb-8">
              Não espere por um incidente de segurança. Entre em contato hoje mesmo 
              e garante a proteção dos seus dados e sistemas.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={openWhatsApp}
                className="btn-primary"
              >
                Falar com Especialista
              </button>
              
              <a
                href={`mailto:${mockData.contact.email}`}
                className="btn-secondary"
              >
                Enviar E-mail
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;